import { useContext } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'

import { trackInternalEvent } from '@rio/tracking'
import { useLocation } from 'react-router-dom'
import { AxiosError } from 'axios'
import { baseClient } from 'builder/modules/apiClient'
import { actions as uiActions } from 'builder/modules/ui'
import { SnackBarContext, SnackBarTypes } from 'builder/components/SnackBarV2'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { useI18n } from 'builder/hooks/useI18n'
import { TRANSLATION } from '../utils'

type Props = {
  onErrorState?: () => void
  onLoadingState?: () => void
  showSnackbarError?: boolean
  showSnackbarLoading?: boolean
}

type Payload = {
  cmsgid?: string
}

type Response = {
  WUID: string
  redirect_url: string
}

type ErrorResponse = {
  response: {
    missing_parameters: {
      first_name: string
      last_name: string
      job_title: string
    }
  }
}

function resolveErrorMessage(error: AxiosError) {
  if (error instanceof AxiosError) {
    const errorCode = error.code

    switch (errorCode) {
      case 'ERR_BAD_REQUEST':
        return `${TRANSLATION}.errors.bad_request`
      // In case we need other switch cases
      default:
        return `${TRANSLATION}.errors.redirect`
    }
  }

  return `${TRANSLATION}.errors.generic`
}

export const usePostRedirectUrl = ({
  onErrorState,
  onLoadingState,
  showSnackbarError = true,
  showSnackbarLoading = true,
}: Props = {}) => {
  const { i18n } = useI18n()
  const { features } = useFeaturesConfig()
  const { openSnackBar } = useContext(SnackBarContext)
  const { pathname } = useLocation()
  const dispatch = useDispatch()

  const desktopBottom = features?.aiCoach && pathname === '/' ? 96 : 16

  const fetchRedirectUrl = async (payload?: Payload): Promise<Response> => {
    const res = await baseClient.post('/learning/users/create_redirect_link', payload)
    return res.data
  }

  const onMutate = () => {
    onLoadingState?.()
    if (!showSnackbarLoading) return
    openSnackBar({
      text: 'Loading, please wait…',
      type: SnackBarTypes.loading,
      bottom: { phone: 96, desktop: desktopBottom },
    })
  }

  const getMissingParameters = (
    error: AxiosError,
  ): ErrorResponse['response']['missing_parameters'] | Record<string, never> => {
    if (error instanceof AxiosError && error.code === 'ERR_BAD_REQUEST') {
      const errorResponse = error.response?.data as ErrorResponse
      return errorResponse?.response?.missing_parameters || {}
    }
    return {}
  }

  const onError = (error: AxiosError, payload: Payload) => {
    onErrorState?.()

    const {
      first_name: firstName,
      last_name: lastName,
      job_title: jobTitle,
    } = getMissingParameters(error)

    if (firstName || lastName) {
      trackInternalEvent('learning_hub_error', {
        type: 'redirect',
        missing_parameters: 'first_name, last_name',
      })

      dispatch(
        uiActions.toggleFutureLearnFullNameModal({
          open: true,
          modal: 'full_name',
          cmsgid: payload.cmsgid,
        }),
      )
      return
    }

    if (jobTitle) {
      trackInternalEvent('learning_hub_error', {
        type: 'redirect',
        missing_parameters: 'job_title',
      })
      dispatch(
        uiActions.toggleFutureLearnFullNameModal({
          open: true,
          modal: 'job_title',
          cmsgid: payload.cmsgid,
        }),
      )
      return
    }

    trackInternalEvent('learning_hub_error', { type: 'redirect' })

    if (!showSnackbarError) return
    const errorMessage = resolveErrorMessage(error)

    openSnackBar({
      text: i18n.t(errorMessage),
      type: SnackBarTypes.failure,
      bottom: { phone: 96, desktop: desktopBottom },
    })
  }

  const mutation = useMutation({
    mutationKey: ['learning-hub-redirect-url'],
    mutationFn: fetchRedirectUrl,
    onMutate,
    onError,
  })

  const mutateAndRedirect = (props: { cmsgid?: string }) => {
    mutation
      .mutateAsync(props)
      .then(response => window.open(response.redirect_url, '_blank'))
      .catch(error => {
        // No-op since it is handled on the onError callback above
        console.error({ error })
      })
  }

  return { ...mutation, mutateAndRedirect }
}
